/**
 * usePermissions Hook
 *
 * Centralized hook for role-based permission checks.
 * Combines scope context with the permissions configuration to provide
 * a single source of truth for permission-based UI decisions.
 *
 * Roles:
 * - "mentor": User is masquerading as a client (viewing client's data)
 * - "client": User is a client viewing mentor-created content
 * - "regularUser": Standard user viewing their own data
 *
 * @returns {Object} Returns:
 *   - role: Current role (mentor, client, regularUser)
 *   - permissions: Full permissions object for current role
 *   - hasPermission: Function to check a specific permission
 *   - canDeletePlans: Boolean - shortcut for common permission check
 *   - canCreatePlans: Boolean - shortcut for common permission check
 *   - canEditPlans: Boolean - shortcut for common permission check
 *   - canViewPlans: Boolean - shortcut for common permission check
 *   - canManageClients: Boolean - shortcut for common permission check
 */

import { useMemo, useCallback } from "react";
import { useScope } from "./useScope";
import {
  getPermissions,
  hasPermission as checkPermission,
} from "../config/permissions";

export const usePermissions = () => {
  const { getRole, isManaged, isClientView } = useScope();

  // Get current role from scope context
  const role = useMemo(() => getRole(), [getRole]);

  // Get permissions object for current role
  const permissions = useMemo(() => getPermissions(role), [role]);

  /**
   * Check if current role has a specific permission
   * @param {string} permissionName - Name of the permission to check
   * @returns {boolean|string} Permission value
   */
  const hasPermission = useCallback(
    (permissionName) => {
      return checkPermission(role, permissionName);
    },
    [role]
  );

  // Common permission shortcuts for convenience
  const canDeletePlans = permissions.canDeletePlans === true;
  const canCreatePlans = permissions.canCreatePlans === true;
  const canEditPlans = permissions.canEditPlans === true;
  const canViewPlans = permissions.canViewPlans === true;
  const canManageClients = permissions.canManageClients === true;
  const canManageInventory = permissions.canManageInventory === true;
  const canAccessAIChat = permissions.canAccessAIChat === true;
  const canAccessActionable = permissions.canAccessActionable === true;
  const canAccessDemos = permissions.canAccessDemos === true;
  const knowledgeBaseAccess = permissions.canAccessKnowledgeBase || "full";

  return {
    // Role info
    role,
    isManaged,
    isClientView,

    // Full permissions object
    permissions,

    // Permission check function
    hasPermission,

    // Common permission shortcuts
    canDeletePlans,
    canCreatePlans,
    canEditPlans,
    canViewPlans,
    canManageClients,
    canManageInventory,
    canAccessAIChat,
    canAccessActionable,
    canAccessDemos,
    knowledgeBaseAccess,
  };
};

export default usePermissions;

